import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { KeyRound, X } from 'lucide-react';
import { Button } from './button';
import PasswordInput, { generateSecurePassword, passwordMeetsRules } from './PasswordInput';

const PasswordField = ({ id, label, ...props }) => (
  <div className="space-y-1.5">
    <label htmlFor={id} className="text-xs font-semibold text-zinc-800">{label}</label>
    <PasswordInput id={id} {...props} />
  </div>
);

const PasswordChangeDialog = ({ open, onClose, onSubmit }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [visibleFields, setVisibleFields] = useState({});
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  if (!open) return null;

  const rulesAreValid = passwordMeetsRules(newPassword);
  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    if (!currentPassword) {
      setError('Shkruaj fjalëkalimin aktual.');
      return;
    }
    if (!rulesAreValid) {
      setError('Fjalëkalimi i ri nuk i plotëson kërkesat.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Fjalëkalimet e reja nuk përputhen.');
      return;
    }

    setIsSaving(true);
    try {
      const result = await onSubmit(currentPassword, newPassword);
      if (result === false || result?.success === false) {
        setError(result?.error || 'Fjalëkalimi nuk u ndryshua. Kontrollo fjalëkalimin aktual dhe provo përsëri.');
        return;
      }
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      onClose();
    } catch (submitError) {
      console.error('Unable to change account password:', submitError);
      setError('Fjalëkalimi nuk u ndryshua. Provo përsëri.');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleVisibility = (field) => {
    setVisibleFields((previous) => ({ ...previous, [field]: !previous[field] }));
  };

  const closeDialog = () => {
    if (isSaving) return;
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setVisibleFields({});
    setError('');
    onClose();
  };

  const generatePassword = () => {
    try {
      const generatedPassword = generateSecurePassword();
      setNewPassword(generatedPassword);
      setConfirmPassword(generatedPassword);
      setVisibleFields((previous) => ({ ...previous, next: true, confirm: true }));
      setError('');
    } catch (generationError) {
      console.error('Unable to generate a secure password:', generationError);
      setError('Nuk u gjenerua fjalëkalimi i sigurt. Provo përsëri.');
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[1100] flex min-h-screen min-w-full items-center justify-center bg-zinc-950/45 p-4 backdrop-blur-md transition-all duration-300">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="password-dialog-title"
        className="w-full max-w-lg overflow-hidden rounded-2xl border border-zinc-200/80 dark:border-white/15 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-2xl backdrop-saturate-150 text-zinc-950 dark:text-zinc-50 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.25)] animate-scaleIn"
      >
        <header className="flex items-start justify-between border-b border-zinc-200/60 dark:border-white/10 px-5 py-4 bg-white/40 dark:bg-zinc-800/40 backdrop-blur-xs">
          <div>
            <h2 id="password-dialog-title" className="text-lg font-semibold">Ndrysho fjalëkalimin</h2>
            <p className="mt-1 text-sm text-zinc-500">Shkruani fjalëkalimin aktual, pastaj zgjidhni një të ri.</p>
          </div>
          <button
            type="button"
            onClick={closeDialog}
            className="rounded-lg p-1.5 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
            aria-label="Mbyll"
          >
            <X size={18} />
          </button>
        </header>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 px-5 py-5">
            <PasswordField
              id="current-password"
              label="Fjalëkalimi aktual"
              value={currentPassword}
              onChange={setCurrentPassword}
              visible={Boolean(visibleFields.current)}
              onVisibleChange={() => toggleVisibility('current')}
              autoComplete="current-password"
            />
            <PasswordField
              id="new-password"
              label="Fjalëkalimi i ri"
              value={newPassword}
              onChange={setNewPassword}
              visible={Boolean(visibleFields.next)}
              onVisibleChange={() => toggleVisibility('next')}
              onGenerate={generatePassword}
              showStrength
              autoComplete="new-password"
            />

            <PasswordField
              id="confirm-password"
              label="Konfirmo fjalëkalimin"
              value={confirmPassword}
              onChange={setConfirmPassword}
              visible={Boolean(visibleFields.confirm)}
              onVisibleChange={() => toggleVisibility('confirm')}
              invalid={Boolean(confirmPassword) && confirmPassword !== newPassword}
              autoComplete="new-password"
            />
            {error && <p role="alert" className="text-xs text-red-600">{error}</p>}
          </div>

          <footer className="flex justify-end gap-2 border-t border-zinc-200/60 dark:border-white/10 bg-zinc-50/70 dark:bg-zinc-800/40 px-5 py-3 backdrop-blur-xs">
            <Button type="button" variant="outline" size="sm"             onClick={closeDialog} disabled={isSaving}>
              Anulo
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={isSaving || !currentPassword || !rulesAreValid || !confirmPassword}
              className="bg-zinc-950 text-[#FFCE00] hover:bg-[#DD0000] hover:text-white"
            >
              <KeyRound size={14} />
              {isSaving ? 'Duke ndryshuar...' : 'Ndrysho fjalëkalimin'}
            </Button>
          </footer>
        </form>
      </section>
    </div>,
    document.body
  );
};

export default PasswordChangeDialog;
